import React, { ReactNode, FC, useState, useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';

import { WithTranslation } from 'react-i18next';
import { MainContentProps } from 'ia-react-core/~/components/layout/MainContent';
import T2 from 'ia-react-core/~/components/titles/T2';
import Promo2 from 'ia-react-core/~/components/titles/Promo2';
import getAppContainerId from 'ia-react-core/~/utils/dom/getAppContainerId';
import SchemaContext from 'ia-react-core/~/components/schema/_SchemaContext';
import ColorSchemeContext from 'ia-react-core/~/components/colorScheme/ColorSchemeContext/ColorSchemeContext';
import COLOR_SCHEMES from 'ia-react-core/~/constants/COLOR_SCHEMES/COLOR_SCHEMES';
import { ColorScheme } from 'ia-react-core/~/interfaces/ColorScheme';
import RootContext from 'ia-react-core/~/components/config/_RootContext';
import Router from 'ia-react-core/~/services/router/Router';
import useResize from '../../hooks/_useResize';

import openDrawerByName from '../../actions/drawer-fn/openDrawerByName';
import closeDrawerByName from '../../actions/drawer-fn/closeDrawerByName';
import changeLanguage from '../../actions/fn/changeLanguage';

import MainHeader, { MainHeaderProps } from '../../components/layout/MainHeader';
import MainFooter from '../../components/layout/MainFooter';
import FooterLinks, { FooterLinksProps } from '../../components/layout/FooterLinks';
import SideNav from '../../components/navigation/SideNav';
import Drawer from '../../components/drawer/Drawer';

import { ReactComponent as MenuIcon } from '../../icons/menu.svg';
import findChildByType from '../../utils/components/_findChildByType';
import excludeChildrenByType from '../../utils/components/_excludeChildrenByType';

import {
  StyledSideNavLayout, StyledContentWrapper, StyledContent, StyledNavCol, StyledContentCol, StyledIconButton, StyledContentNavWrapper, StyledContentNavContentWrapper, LayoutGlobalStyles,
} from './SideNavLayout.styles';
import { BeforeHeader, Content, DrawerFooter, HeaderRightContent } from './SideNavLayout.compounds';

export interface SideNavLayoutProps {
  /** Overrides the copyright text. */
  copyrightText?: ReactNode;
  /** App name to display next to the logo. */
  appName?: string;
  /** Background of the content. */
  contentBackground?: string;
  /** Remove the language button when component is in drawer mode. */
  noLanguageButtonNav?: boolean;
  /** Content of the layout. */
  children?: ReactNode;
  /** List of external links. Prefer `linksUrl` when possible. */
  mainFooterLinks?: FooterLinksProps['links'];
  /** Links for the Sitecore service returning the footer links. */
  mainFooterLinksUrl?: string;
  /** The url of the links when it is not specified. */
  mainFooterLinksDomain?: string;
  /** Hide the loading of the footer links. */
  noMainFooterLinksLoader?: boolean;
  /** List of buttons to display to the right of the links. */
  mainFooterButtons?: FooterLinksProps['buttons'];
  /** Breakpoint from which the sideNav shows. */
  sideNavBreakpoint?: number;
  /** Height css of the content */
  contentHeight?: string;
  /** Minimum content width */
  minContentWidth?: MainContentProps['minContentWidth'];
  /** Maximum content width */
  maxContentWidth?: MainContentProps['maxContentWidth'];
  /** Horizontal padding */
  contentHorizontalPadding?: number;
  /** Vertical padding */
  contentVerticalPadding?: number;
  /** Remove the content padding */
  contentNoPadding?: boolean;
  /** Force the sideNav visibility. */
  sideNavVisible?: boolean;
  /** Put the language button on the same row as the slogan */
  languageButtonInlineWithSlogan?: boolean;
  /** Minimum width of the nav. */
  minWidth?: number;
  /** Maximum width of the nav. */
  maxWidth?: number;
  /** Background of the side nav.. */
  navColBackground?: string;
  /** Background of the side nav.. */
  navTopBorder?: string;
  /** Position of the footer. */
  footerPosition?: 'content' | 'layout';
  /** Position of the SideNav. */
  sideNavPosition?: 'content' | 'contentWrapper';
  /** Padding to the right of the SideNav. */
  sideNavColRightPadding?: number;
  /** Shows the footer in the nav drawer. */
  showFooterInDrawer?: boolean;
  /** If the button to open the navigation is visible on mobile. */
  mobileNavButtonVisible?: boolean;
  /** Split the main header into 2 headers on mobile. */
  showSecondaryHeaderMobile?: boolean;
  /** The title to show in the drawer. If not provided, will display the appName. */
  drawerTitle?: ReactNode;
  /** Removes the paddind of the drawer's content. */
  noDrawerContentPadding?: boolean;
  /** Bottom border of the Drawer header. */
  drawerHeaderBottomBorder?: boolean | string;
  /** Padding of the Drawer header. */
  drawerHeaderPadding?: string;
  /** Padding of the Drawer content. */
  drawerContentPadding?: string;
  /** Drawer max width. */
  drawerMaxWidth?: number;
  /** Stretch the content to fill the avaiable height. */
  fullContentHeight?: boolean;
  /** Callback triggered when the main logo is clicked. */
  onMainLogoClick?: MainHeaderProps['onMainLogoClick'];
  /** @ignore */
  sideNavLayoutId?: number;

}

interface SideNavLayoutInternalProps extends WithTranslation { }

export interface SideNavLayoutComposedProps extends SideNavLayoutProps, SideNavLayoutInternalProps {}

const SideNavLayoutDefaultProps: Partial<SideNavLayoutProps> = {
  sideNavBreakpoint: 1360,
  minWidth: 256,
  maxWidth: 300,
  sideNavVisible: true,
  footerPosition: 'content',
  sideNavPosition: 'contentWrapper',
  showFooterInDrawer: true,
  mobileNavButtonVisible: true,
  showSecondaryHeaderMobile: true,
  noDrawerContentPadding: true,
} as const;

export type SideNavLayoutStatics = {
  Content: typeof Content;
  BeforeHeader: typeof BeforeHeader;
  DrawerFooter: typeof DrawerFooter;
  HeaderRightContent: typeof HeaderRightContent;
  SideNav: typeof SideNav;
  appElement?: string | HTMLElement;
  setAppElement (appElement: HTMLElement | string): void;
};

export interface SideNavLayoutMock {}

const SideNavLayout: FC<SideNavLayoutComposedProps> & SideNavLayoutStatics = ({
  sideNavLayoutId, t, tReady, children, copyrightText, appName, contentBackground, noLanguageButtonNav, mainFooterLinks, mainFooterLinksUrl, mainFooterLinksDomain, noMainFooterLinksLoader, mainFooterButtons, sideNavBreakpoint, contentHeight, minContentWidth, maxContentWidth, contentHorizontalPadding, contentVerticalPadding, contentNoPadding, sideNavVisible, languageButtonInlineWithSlogan, minWidth, maxWidth, navColBackground, footerPosition, navTopBorder, showFooterInDrawer, mobileNavButtonVisible, showSecondaryHeaderMobile, sideNavPosition, drawerTitle, noDrawerContentPadding, sideNavColRightPadding, drawerHeaderBottomBorder, drawerHeaderPadding, drawerContentPadding, drawerMaxWidth, fullContentHeight, onMainLogoClick, ...props
}) => {
  const { root } = useContext(RootContext);

  // Do not use useEffect for instant execution
  if (!SideNavLayout.appElement) {
    const element: HTMLElement = root.querySelector(`#${getAppContainerId()}`);
    SideNavLayout.setAppElement(element);
  }

  const rootNodeRef = useRef(
    typeof SideNavLayout.appElement === 'string' ?
      root.querySelector(SideNavLayout.appElement) :
      SideNavLayout.appElement,
  );
  const dispatch = useDispatch();
  const location = Router.useLocation();
  const [isInitialRender, setIsInitialRender] = useState(true);

  const layoutRef = useRef<HTMLDivElement>();

  const bigScreen = useCallback(() => rootNodeRef.current.clientWidth >= sideNavBreakpoint, [sideNavBreakpoint]);

  const [isBigScreen, setIsBigScreen] = useState(bigScreen);

  useResize(rootNodeRef, () => {
    setIsBigScreen(bigScreen);
  });

  const { schema } = useContext(SchemaContext);
  const drawerName = useMemo(() => `${schema.extendName('drawer', { name: 'sideNavLayout' })}${sideNavLayoutId}`, [schema, sideNavLayoutId]);

  const openDrawer = useCallback(() => {
    dispatch(openDrawerByName(drawerName));
  }, [dispatch, drawerName]);

  const headerHeight = useMemo(() => 60, []);
  const verticalPadding = useMemo(() => contentVerticalPadding || 60, [contentVerticalPadding]);

  const dispatchChangeLanguage = useCallback((language: 'fr' | 'en') => dispatch(changeLanguage(language)), [dispatch]);

  const handleFooterLanguageChange = useCallback((nextLanguage: 'fr' | 'en') => {
    dispatchChangeLanguage(nextLanguage);
    layoutRef?.current.scrollTo(0, 0);
  }, [dispatchChangeLanguage]);

  const beforeHeader = findChildByType(children, BeforeHeader);
  const sideNav = findChildByType(children, SideNav);
  const content = excludeChildrenByType(children, BeforeHeader, SideNav, DrawerFooter, HeaderRightContent);
  const drawerFooter = findChildByType(children, DrawerFooter);
  const headerRightContent = findChildByType(children, HeaderRightContent);

  const footer = useMemo(() => (
    <MainFooter
      copyrightText={copyrightText}
      alignWithContent={sideNavVisible}
      linksUrl={mainFooterLinksUrl}
      linksDomain={mainFooterLinksDomain}
      links={mainFooterLinks}
      noLinksLoader={noMainFooterLinksLoader}
      buttons={mainFooterButtons}
      noLanguageButton={false}
      onLanguageChange={handleFooterLanguageChange}
      languageButtonInlineWithSlogan={languageButtonInlineWithSlogan}
    />
  ), [copyrightText, handleFooterLanguageChange, languageButtonInlineWithSlogan, mainFooterButtons, mainFooterLinks, mainFooterLinksDomain, mainFooterLinksUrl, noMainFooterLinksLoader, sideNavVisible]);

  useEffect(() => {
    if (!isInitialRender) {
      dispatch(closeDrawerByName(drawerName));
    } else {
      setIsInitialRender(false);
    }
  }, [dispatch, drawerName, location, isInitialRender]);

  const sideNavCol = useMemo(() => (
    sideNavVisible && isBigScreen && (
      <StyledNavCol
        minWidth={minWidth}
        maxWidth={maxWidth}
        navColBackground={navColBackground}
        sideNavPosition={sideNavPosition}
        sideNavColRightPadding={sideNavColRightPadding}
      >
        {sideNav}
      </StyledNavCol>
    )
  ), [isBigScreen, maxWidth, minWidth, navColBackground, sideNav, sideNavPosition, sideNavVisible, sideNavColRightPadding]);

  const colorSchemeContext = useMemo<{ scheme: ColorScheme }>(() => ({ scheme: COLOR_SCHEMES.SHADE }), []);

  return (
    <>
      <LayoutGlobalStyles />
      <StyledSideNavLayout
        ref={layoutRef}
        isBigScreen={isBigScreen}
        navTopBorder={navTopBorder}
        {...props}
      >
        {beforeHeader}
        {!isBigScreen && (
          <>
            {showSecondaryHeaderMobile && (
              <MainHeader
                height={headerHeight}
                horizontalLogo
              />
            )}
            {sideNavVisible && (
              <Drawer
                name={drawerName}
                noContentPadding={noDrawerContentPadding}
                drawerTitle={drawerTitle || appName}
                headerBottomBorder={drawerHeaderBottomBorder}
                headerPadding={drawerHeaderPadding}
                contentPadding={drawerContentPadding}
                maxWidth={drawerMaxWidth}
              >
                <Drawer.Content>
                  {sideNav}
                </Drawer.Content>
                {showFooterInDrawer && (
                  <Drawer.Footer>
                    <FooterLinks
                      linksUrl={mainFooterLinksUrl}
                      linksDomain={mainFooterLinksDomain}
                      links={mainFooterLinks}
                      noLinksLoader={noMainFooterLinksLoader}
                      buttons={mainFooterButtons}
                      noLanguageButton={noLanguageButtonNav || false}
                      onLanguageChange={handleFooterLanguageChange}
                      vertical
                    />

                    {drawerFooter}
                  </Drawer.Footer>
                )}
              </Drawer>
            )}
          </>
        )}
        <MainHeader
          height={headerHeight}
          horizontalLogo
          primary
          noLogo={!isBigScreen && showSecondaryHeaderMobile}
          onMainLogoClick={onMainLogoClick}
        >
          {sideNavVisible && !isBigScreen && mobileNavButtonVisible && (
            <MainHeader.LeftContent>
              <StyledIconButton
                Icon={MenuIcon}
                data-testid="SideNavLayoutMenuButton"
                aria-label={t('openMenu')}
                onClick={openDrawer}
              />
            </MainHeader.LeftContent>
          )}
          {appName && (
            <MainHeader.MiddleContent>
              {appName}
            </MainHeader.MiddleContent>
          )}
          {headerRightContent && (
            <MainHeader.RightContent>
              {headerRightContent}
            </MainHeader.RightContent>
          )}
        </MainHeader>
        <StyledContentWrapper contentBackground={contentBackground}>
          {sideNavPosition === 'contentWrapper' ? sideNavCol : null}
          <StyledContentCol>
            <ColorSchemeContext.Provider value={colorSchemeContext}>
              <StyledContent
                appName={appName}
                PrimaryTitle={Promo2}
                SecondaryTitle={T2}
                background="transparent"
                contentHeight={contentHeight}
                verticalPadding={verticalPadding}
                minContentWidth={minContentWidth}
                maxContentWidth={maxContentWidth}
                horizontalPadding={contentHorizontalPadding}
                nopadding={contentNoPadding}
                fullHeight={fullContentHeight}
              >
                {sideNavPosition === 'content' ? (
                  <StyledContentNavWrapper
                    fullHeight={fullContentHeight}
                  >
                    {sideNavCol}
                    <StyledContentNavContentWrapper
                      fullHeight={fullContentHeight}
                    >
                      {content}
                    </StyledContentNavContentWrapper>
                  </StyledContentNavWrapper>
                ) : content}
              </StyledContent>
            </ColorSchemeContext.Provider>
            {footerPosition === 'content' ? footer : null}
          </StyledContentCol>
        </StyledContentWrapper>
        {footerPosition === 'layout' ? footer : null}
      </StyledSideNavLayout>
    </>
  );
};

SideNavLayout.BeforeHeader = BeforeHeader;
SideNavLayout.SideNav = SideNav;
SideNavLayout.Content = Content;
SideNavLayout.DrawerFooter = DrawerFooter;
SideNavLayout.HeaderRightContent = HeaderRightContent;

SideNavLayout.defaultProps = SideNavLayoutDefaultProps;

SideNavLayout.setAppElement = (appElement: string) => {
  SideNavLayout.appElement = appElement;
};

export default SideNavLayout;
